
import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export const useSubscriptionInitializer = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkExistingSubscription = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // Not logged in, stay on paywall
          setLoading(false);
          return;
        }

        // Check for active subscription
        const { data: subscription, error } = await supabase
          .from('subscriptions')
          .select('id, status, plan_type, trial_ends_at, current_period_end')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking subscription:', error);
          setLoading(false);
          return;
        }

        if (subscription) {
          // Handle trial period
          if (subscription.status === 'trialing' && subscription.trial_ends_at) {
            const trialEndsAt = new Date(subscription.trial_ends_at);
            if (trialEndsAt > new Date()) {
              console.log('Active trial found, redirecting to dashboard');
              navigate('/dashboard', { replace: true });
              return;
            }
          }

          // Handle active subscriptions
          if (subscription.status === 'active' || 
              (subscription.plan_type === 'lifetime' && subscription.status !== 'canceled')) {
            console.log('Active subscription found, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
          
          // Handle canceled subscription with remaining access time
          if (subscription.status === 'canceled' && 
              subscription.current_period_end &&
              new Date(subscription.current_period_end) > new Date()) {
            console.log('Canceled subscription with remaining access found, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setLoading(false);
      }
    };

    const initializeStripe = async () => {
      try {
        const { data: { publicKey }, error } = await supabase
          .functions.invoke('get-stripe-key');
        
        if (error) throw error;
        
        if (publicKey) {
          setStripePromise(loadStripe(publicKey));
        }
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        toast({
          title: "Error",
          description: "Failed to initialize payment system",
          variant: "destructive",
        });
      }
    };

    checkExistingSubscription();
    initializeStripe();
  }, [navigate, toast]);

  return { stripePromise, loading };
};
